/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║                       SYNC ID GENERATOR                                   ║
 * ║                                                                           ║
 * ║  ⚠️  CENTRALIZED MODULE - DO NOT DUPLICATE THESE FUNCTIONS!               ║
 * ║                                                                           ║
 * ║  Import: import { generateSyncId, ... } from '@/utils/syncIdGenerator'    ║
 * ║  Or:     import { generateSyncId, ... } from '@/lib'                      ║
 * ║                                                                           ║
 * ║  ESLint will WARN if you try to create local copies of these functions.   ║
 * ║                                                                           ║
 * ║  See: src/lib/README.md for full documentation                            ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 * 
 * Sync ID Generator for idempotent offline requests.
 * Ensures duplicate requests can be detected server-side.
 * 
 * CRASH-SAFE: All operations are pure functions
 */

/**
 * Generate a unique sync ID for a request
 * Format: timestamp-random-hash
 */
export function generateSyncId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 11);
  const hash = generateSimpleHash(`${timestamp}${random}${performance.now()}`);
  
  return `${timestamp}-${random}-${hash}`;
}

/**
 * Generate a simple hash from a string
 */
function generateSimpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * Parse a sync ID to extract timestamp
 */
export function parseSyncId(syncId: string): {
  timestamp: number;
  random: string;
  hash: string;
} | null {
  try {
    const parts = syncId.split('-');
    if (parts.length !== 3) return null;
    
    return {
      timestamp: parseInt(parts[0], 36),
      random: parts[1],
      hash: parts[2],
    };
  } catch {
    return null;
  }
}

/**
 * Check if a sync ID is expired (older than given hours)
 */
export function isSyncIdExpired(syncId: string, maxAgeHours = 24): boolean {
  const parsed = parseSyncId(syncId);
  if (!parsed) return true;
  
  const ageMs = Date.now() - parsed.timestamp;
  const maxAgeMs = maxAgeHours * 60 * 60 * 1000;
  
  return ageMs > maxAgeMs;
}

/**
 * Create data with sync ID attached
 */
export function withSyncId<T extends object>(data: T): T & { _syncId: string; _syncTimestamp: number } {
  return {
    ...data,
    _syncId: generateSyncId(),
    _syncTimestamp: Date.now(),
  };
}

/**
 * Storage for tracking processed sync IDs (client-side)
 */
const PROCESSED_SYNC_IDS_KEY = 'processed-sync-ids';
const MAX_STORED_IDS = 1000;

export function markSyncIdProcessed(syncId: string): void {
  try {
    const stored = localStorage.getItem(PROCESSED_SYNC_IDS_KEY);
    let ids: string[] = stored ? JSON.parse(stored) : [];
    
    // Add new ID
    ids.push(syncId);
    
    // Limit stored IDs
    if (ids.length > MAX_STORED_IDS) {
      ids = ids.slice(-MAX_STORED_IDS);
    }
    
    localStorage.setItem(PROCESSED_SYNC_IDS_KEY, JSON.stringify(ids));
  } catch {
    // Ignore storage errors
  }
}

export function isSyncIdProcessed(syncId: string): boolean {
  try {
    const stored = localStorage.getItem(PROCESSED_SYNC_IDS_KEY);
    if (!stored) return false;
    
    const ids: string[] = JSON.parse(stored);
    return ids.includes(syncId);
  } catch {
    return false;
  }
}

export function clearProcessedSyncIds(): void {
  try {
    localStorage.removeItem(PROCESSED_SYNC_IDS_KEY);
  } catch {
    // Ignore
  }
}
